import {
    useCallback, useEffect, useMemo, useContext
} from 'react';
import _ from 'lodash';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import SgAddon from 'sg-addon';
import {
    SgCEPAddonContent,
    SgVoluntaryExcessCoverageContent,
    SgCEPBreakdownAddonContent
} from 'saga-cep-specific-components';
import SpmConstants from 'sg-spm-constants';
import CommonConstants from 'sg-common-constants';
import {
    SgActiveTestsContext,
    SgProductService
} from 'saga-modules';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import SpmUtils from 'sg-spm-utils';
import metadata from './SgCepMotorAddons.metadata.json5';
import messages from './SgCepMotorAddons.messages';

function SgCepMotorAddons(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        updateCoveragesAndCalculate,
        onValidate: onValidateParent
    } = props;

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);
    const { activeMvtTests } = useContext(SgActiveTestsContext);

    const { spmVehicle } = modelVM.lobData.privateMotor.coverables;
    const vehicleType = SgProductService.isMotorHome() ? SpmConstants.MOTORHOME : SpmConstants.CAR;
    const addOnsCoverages = modelVM.lobData.privateMotor.offerings.children[0].coverages.vehicleCoverages.children[0].coverages;
    const addonCoveragePath = 'lobData.privateMotor.offerings.children[0].coverages.vehicleCoverages.children[0].coverages';

    const coverLevel = useMemo(() => {
        const coverLevelType = viewModelService.productMetadata.get('pc').types
            .getTypelist('SPMBasicCoverOptions_Ext').getCode(spmVehicle.coverLevel.value.code).name;
        return translator({ id: coverLevelType, defaultMessage: coverLevelType }).toString();
    }, [spmVehicle.coverLevel.value.code, translator, viewModelService.productMetadata]);

    const isThirdPartyCoverlevel = useMemo(() => {
        return SpmUtils.isThirdPartyOnly(modelVM) || SpmUtils.isThirdPartyFireAndTheft(modelVM);
    }, [modelVM]);

    const updateAddon = useCallback((addon) => {
        const addons = addOnsCoverages.value;
        const changedAddonIndex = addons.findIndex((el) => el.patternCode === addon.patternCode);
        addons[changedAddonIndex] = addon;
        addons[changedAddonIndex].updated = true;
        addOnsCoverages.value = addons;
        updateData(modelVM);
        if ((activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_1169) || activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_4177))
            && updateCoveragesAndCalculate && (addon.patternCode !== SpmConstants.BREAKDOWN_CODE
            || (addon.patternCode === SpmConstants.BREAKDOWN_CODE && !addon.selected))) {
            updateCoveragesAndCalculate();
        }
    }, [addOnsCoverages, updateData, updateCoveragesAndCalculate, activeMvtTests, modelVM]);

    const onAddonChange = useCallback((addon, newValue) => {
        const addonsNewValue = newValue !== undefined ? newValue : !addon.selected;
        _.set(addon, 'selected', addonsNewValue);
        updateAddon(addon);
    }, [updateAddon]);

    const commonAddonProps = {
        selectLabel: messages.AddCover,
        removeLabel: messages.RemoveCover,
        onChange: onAddonChange,
        showPrice: true,
        showMonthly: false,
        showIncludedTick: false,
        includedLabel: messages.Included,
        updateData: updateData,
        modelVM: modelVM
    };

    const overrideProps = {
        coverLevelText: {
            content: translator(messages.CoverLevelText, { coverLevel: coverLevel })
        },
        legalAddon: {
            ...commonAddonProps,
            isIncluded: spmVehicle.value.coverLevel === SpmConstants.SAGA_PLUS_CODE,
            showPrice: spmVehicle.value.coverLevel !== SpmConstants.SAGA_PLUS_CODE,
            contentMessages: {
                Name: messages.LegalName,
                Description: messages.LegalDescription
            },
            addon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.LEGAL_PROTECTION_CODE),
            contentComponent: SgCEPAddonContent
        },
        keyAddon: {
            ...commonAddonProps,
            isIncluded: spmVehicle.value.coverLevel === SpmConstants.SAGA_PLUS_CODE,
            showPrice: spmVehicle.value.coverLevel !== SpmConstants.SAGA_PLUS_CODE,
            contentMessages: {
                Name: messages.KeyName,
                Description: messages.KeyDescription
            },
            addon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.KEY_PROTECTION_CODE),
            contentComponent: SgCEPAddonContent,
            visible: !isThirdPartyCoverlevel
        },
        volExcess: {
            ...commonAddonProps,
            isIncluded: true,
            contentMessages: { Name: messages.VolExcessName, Description: messages.VolExcessDescription },
            addon: { selected: true, patternCode: SpmConstants.VOLUNTARY_AD_EXCESS_NAME },
            contentComponent: SgVoluntaryExcessCoverageContent,
            visible: !isThirdPartyCoverlevel,
            updateCoveragesAndCalculate: updateCoveragesAndCalculate
        },
        personalAccidentAddon: {
            ...commonAddonProps,
            contentMessages: {
                Name: messages.PersonalAccidentName,
                Description: messages.PersonalAccidentDescription,
                Params: { vehicle: vehicleType }
            },
            addon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.PERSONAL_ACCIDENT_CODE),
            contentComponent: SgCEPAddonContent,
            visible: !isThirdPartyCoverlevel
        },
        replacementCarAddon: {
            ...commonAddonProps,
            contentMessages: { Name: messages.ReplacementCarName, Description: messages.ReplacementCarDescription },
            addon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.REPLACEMENT_CAR_CODE),
            contentComponent: SgCEPAddonContent,
            visible: !isThirdPartyCoverlevel
        },
        breakdownAddon: {
            ...commonAddonProps,
            path: addonCoveragePath,
            contentMessages: { Name: messages.BreakdownName, Description: messages.BreakdownDescription },
            addon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.BREAKDOWN_CODE),
            contentComponent: SgCEPBreakdownAddonContent,
            onValidate: onValidate,
            showErrors: showErrors,
            updateCoveragesAndCalculate: updateCoveragesAndCalculate
        }
    };

    const resolvers = {
        resolveCallbackMap: {},
        resolveComponentMap: {
            SgAddon
        }
    };

    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

export default SgCepMotorAddons;
